import React, { useState } from 'react';
import { Search, LogOut, Settings, Building, Zap, CreditCard, ScrollText, Sun, Moon } from 'lucide-react';
import { Badge } from '@/components/ui';
import { Avatar, AvatarFallback } from '@/components/ui/Avatar';
import { Progress } from '@/components/ui/Progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { PLAN_CONFIG } from '@/components/settings/OrgSettingsPanel';
import { useTheme } from '@/providers/ThemeProvider';
import { useNavigate } from 'react-router-dom';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { LiveStatus } from '@/components/topbar/LiveStatus';
import { QuickActionsMenu } from '@/components/topbar/QuickActionsMenu';
import { CommandPalette } from '@/components/search/CommandPalette';
import { NotificationsDropdown } from '@/components/notifications/NotificationsDropdown';
import { useTokenBalance } from '@/hooks/useTokenBalance';
import { cn } from '@/lib/utils';

/**
 * Quota ring/bar color by fill level: green < 50%, orange 50–75%, red ≥ 75%.
 * `bar` must be a full literal class so Tailwind's JIT keeps it.
 */
function quotaColor(percentUsed: number | null) {
  if (percentUsed != null && percentUsed >= 75)
    return { stroke: 'text-status-error', bar: '[&>div]:bg-status-error' };
  if (percentUsed != null && percentUsed >= 50)
    return { stroke: 'text-status-warning', bar: '[&>div]:bg-status-warning' };
  return { stroke: 'text-status-success', bar: '[&>div]:bg-status-success' };
}

/**
 * Avatar wrapped in a circular execution-quota progress ring.
 * Ring renders only on finite plans once the quota has loaded.
 */
function QuotaAvatar({
  initials,
  percentUsed,
  showRing,
}: {
  initials: string;
  percentUsed: number | null;
  showRing: boolean;
}) {
  const size = 36;
  const stroke = 2.5;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, percentUsed ?? 0));
  const offset = circumference * (1 - pct / 100);
  const color = quotaColor(percentUsed);

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      {showRing && (
        <svg
          className="absolute inset-0 -rotate-90"
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          aria-hidden="true"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={stroke}
            className="text-muted"
            stroke="currentColor"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={cn(color.stroke, 'transition-all duration-300')}
            stroke="currentColor"
          />
        </svg>
      )}
      <Avatar className="h-8 w-8">
        <AvatarFallback className="bg-accent-blue text-white font-semibold text-sm">
          {initials}
        </AvatarFallback>
      </Avatar>
    </div>
  );
}

export function TopBar() {
  const { currentUser, userProfile, activeOrganization, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const quota = useTokenBalance();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getUserInitials = () => {
    if (userProfile?.displayName) {
      return userProfile.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    if (currentUser?.email) {
      return currentUser.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const { balance, grant, unlimited, percentUsed, loading: quotaLoading } = quota;
  const showRing = !unlimited && percentUsed != null && !quotaLoading;
  const quotaColors = quotaColor(percentUsed);

  return (
    <header className="sticky top-0 h-16 bg-card border-b border-border dark:border-border-subtle z-40">
      <div className="h-full px-3 sm:px-4 flex items-center gap-2 sm:gap-4">
        {/* Sidebar Trigger & Logo */}
        <div className="flex items-center gap-2 min-w-0">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-1 h-4 hidden sm:block" />
          <img src={theme === 'dark' ? '/logo-dark.png' : '/logo.png'} alt="simcheck.ai" className="w-8 h-8 object-contain shrink-0" />
          <span className="text-lg tracking-tight hidden sm:inline">
            <span className="font-semibold" style={{ color: '#545A5F' }}>simcheck</span>
            <span className="font-medium" style={{ color: '#75797C' }}>.ai</span>
          </span>
        </div>

        {/* Search - desktop: full input button; mobile: icon-only */}
        <div className="flex-1 max-w-2xl mx-auto hidden sm:block">
          <button
            onClick={() => setIsCommandPaletteOpen(true)}
            className="w-full h-10 pl-10 pr-4 bg-background border border-border rounded-lg
                     text-muted-foreground/70 text-sm text-left relative
                     hover:border-accent-blue hover:shadow-glow-focus
                     transition-all duration-fast"
          >
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none" />
            <span>Search flows, modems, executions...</span>
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none
                          inline-flex h-5 items-center gap-1 rounded border
                          bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              <span className="text-xs">⌘</span>K
            </kbd>
          </button>
        </div>
        <button
          onClick={() => setIsCommandPaletteOpen(true)}
          className="sm:hidden flex h-10 w-10 items-center justify-center rounded-lg hover:bg-muted transition-colors"
          aria-label="Search"
        >
          <Search className="h-5 w-5 text-muted-foreground" />
        </button>
        <div className="flex-1 sm:hidden" />

        {/* Right Actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Live Status Indicator */}
          <LiveStatus />

          {/* Notifications */}
          <NotificationsDropdown />

          {/* Quick Actions */}
          <QuickActionsMenu onOpenCommandPalette={() => setIsCommandPaletteOpen(true)} />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-10 h-10 flex items-center justify-center rounded-lg
                         hover:bg-muted transition-colors"
                title="User Menu"
              >
                <QuotaAvatar
                  initials={getUserInitials()}
                  percentUsed={percentUsed}
                  showRing={showRing}
                />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-72">
              {/* User Info */}
              <DropdownMenuLabel className="font-normal">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12 shrink-0">
                    <AvatarFallback className="bg-accent-blue text-white font-bold text-lg">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 overflow-hidden">
                    <div className="font-medium text-foreground truncate">
                      {userProfile?.displayName || 'User'}
                    </div>
                    <div className="text-sm text-muted-foreground truncate font-normal">
                      {currentUser?.email}
                    </div>
                  </div>
                </div>
                {activeOrganization && (
                  <div className="mt-3 pt-3 border-t border-border dark:border-border-subtle">
                    <div className="flex items-center gap-2 text-sm">
                      <Building className="w-4 h-4 text-muted-foreground/70 shrink-0" />
                      <span className="text-muted-foreground truncate flex-1">
                        {activeOrganization.name}
                      </span>
                      {activeOrganization.subscription?.plan && PLAN_CONFIG[activeOrganization.subscription.plan] && (
                        <Badge className={PLAN_CONFIG[activeOrganization.subscription.plan].className + ' text-xs shrink-0'}>
                          {PLAN_CONFIG[activeOrganization.subscription.plan].label}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              {/* Token balance */}
              <div className="px-2 py-1.5">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Zap className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Tokens
                  </span>
                </div>
                <div className="flex items-baseline gap-1.5 mb-2">
                  <span className="text-lg font-semibold text-foreground leading-none">
                    {quotaLoading ? '—' : unlimited ? '∞' : balance != null ? balance.toLocaleString() : '—'}
                  </span>
                  {!unlimited && grant != null && !quotaLoading && (
                    <span className="text-sm text-muted-foreground">/ {grant.toLocaleString()}</span>
                  )}
                </div>
                {!unlimited && (
                  <Progress
                    value={percentUsed ?? 0}
                    className={cn('h-2 mb-1.5', quotaColors.bar)}
                  />
                )}
                <p className="text-xs text-muted-foreground">
                  {quotaLoading
                    ? '—'
                    : unlimited
                      ? 'No limit on your plan'
                      : balance != null
                        ? `${balance.toLocaleString()} tokens remaining · 1 token = 1 test (up to 5 min)`
                        : '—'}
                </p>
                {!quotaLoading && !unlimited && (
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    Each test uses at least 1 token. A token covers 5 minutes of
                    run time — a longer test uses one more token per additional 5
                    minutes.
                  </p>
                )}
              </div>

              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <CreditCard className="w-4 h-4" />
                <span>View billing</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {/* Menu Items */}
              {/* Theme — segmented control (current state always visible) */}
              <div className="px-2 py-1.5">
                <span className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                  Theme
                </span>
                <div className="flex items-center gap-0.5 rounded-md border border-border bg-muted p-0.5">
                  <button
                    type="button"
                    onClick={() => setTheme('light')}
                    aria-pressed={theme === 'light'}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1.5 rounded px-2 py-1 text-sm transition-colors',
                      theme === 'light'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Sun className="w-4 h-4" />
                    <span>Light</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTheme('dark')}
                    aria-pressed={theme === 'dark'}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1.5 rounded px-2 py-1 text-sm transition-colors',
                      theme === 'dark'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Moon className="w-4 h-4" />
                    <span>Dark</span>
                  </button>
                </div>
              </div>

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="w-4 h-4" />
                <span>Settings</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/organization')}>
                <Building className="w-4 h-4" />
                <span>Organization</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/legal/terms')}>
                <ScrollText className="w-4 h-4" />
                <span>Legal</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={handleLogout} className="text-accent-red focus:text-accent-red">
                <LogOut className="w-4 h-4" />
                <span className="font-medium">Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Command Palette */}
      <CommandPalette
        open={isCommandPaletteOpen}
        onOpenChange={setIsCommandPaletteOpen}
      />
    </header>
  );
}
